import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Drawer, Box, Stack, Typography, IconButton, Button,
    Divider, Skeleton, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BadgeIcon from '@mui/icons-material/Badge';
import { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { SplitTabsPanel } from '@/components/common';
import { BalanceSheet, ProfitLoss } from '@/components/fs-report';
import { useFsCategoryStore } from '@/store/audit/fsCategoryStore';
import { buildFsDisplayValuesFromGl, buildGlExcelNameByFsCode, buildGlExcelNameZhByFsCode } from '@/utils/glToFsMapper';
import { resolvePlCotInputMode } from '@/utils/plCostOfSalesVisibility';
import {BalanceSheetOutlinedIcon, ProfitLossOutlinedIcon} from '@/assets/icons/FsStatementIcon';
import { collectGlTinyNavigableFsCodes } from '@/pages/components/account/gl-invoice/mode/GlTinyColumn';

interface FsDrawerProps {
    open: boolean;
    onClose: () => void;
    companyId: number;
    annualId: number;
    columnDefs: GlColumnDef[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    /** 点击科目行 → 关闭 Summary 并跳转 Tiny Mode 对应 tab */
    onNavigateToTiny?: (fsCode: string) => void;
    /** 与 GL Tiny/Original 同源：开=客户别名，关=标准科目名 */
    showClientAlias?: boolean;
    onShowClientAliasChange?: (on: boolean) => void;
}

const PANEL_HEIGHT = 'calc(100vh - 120px)';

export default function FsDrawer(props: FsDrawerProps) {
    const {
        open, onClose, companyId, annualId, columnDefs,
        summaryCurrentPeriod, summaryPriorYear, onNavigateToTiny,
        showClientAlias = false,
        onShowClientAliasChange,
    } = props;

    const {
        loading: sLoading,
        error: sError,
        initIfNeeded,
        byCode,
        bsTree: sBsTree,
        plTree: sPlTree,
        setBsValues,
        setPlValues,
        setBsLastYearValues,
        setPlLastYearValues,
        setGlSuppressPlAfterTaxLastYear,
        setBsRetainedEarningsUseStored,
        setPlCotInputMode,
        setPLComputed,
    } = useFsCategoryStore();
    const loading = !!sLoading;

    const originalValuesRef = useRef<{
        bsValues: Record<number, string>;
        plValues: Record<number, string>;
        bsLastYearValues: Record<number, string>;
        plLastYearValues: Record<number, string>;
        bsRetainedEarningsUseStored: boolean;
    } | null>(null);

    const [valuesInjected, setValuesInjected] = useState(false);

    useEffect(() => {
        if (!open || !companyId || !annualId) return;
        initIfNeeded(companyId, annualId);
    }, [open, companyId, annualId, initIfNeeded]);

    const byCodeReady = Object.keys(byCode).length > 0;
    useEffect(() => {
        if (!open || loading || !byCodeReady) return;
        if (columnDefs.length === 0) return;

        if (!originalValuesRef.current) {
            const state = useFsCategoryStore.getState();
            originalValuesRef.current = {
                bsValues: { ...state.bsValues },
                plValues: { ...state.plValues },
                bsLastYearValues: { ...state.bsLastYearValues },
                plLastYearValues: { ...state.plLastYearValues },
                bsRetainedEarningsUseStored: state.bsRetainedEarningsUseStored,
            };
        }

        const {
            bsValues, plValues, bsLastYearValues, plLastYearValues,
            lfpLastYear,
            suppressPlAfterTaxLastYear,
        } = buildFsDisplayValuesFromGl({
            columnDefs,
            summaryCurrentPeriod,
            summaryPriorYear,
            byCode,
            bsTree: sBsTree,
            plTree: sPlTree,
        });

        // 必须先写入 BS/PL（含上年存货），再设 COS/PUR。
        // 若先改模式，ProfitLoss 会在 OI 仍为 0 时算出 PUR=COS+存货（411013），并 sync 回 store。
        setBsValues(bsValues);
        setPlValues(plValues);
        setBsLastYearValues(bsLastYearValues);
        setPlLastYearValues(plLastYearValues);
        const nextCotMode = resolvePlCotInputMode(null, {
            plTree: sPlTree,
            bsTree: sBsTree,
            plValuesMap: plValues,
            bsValuesMap: bsValues,
            bsLastYearMap: bsLastYearValues,
        });
        setPlCotInputMode(nextCotMode);
        // 留存收益用第 6 行 × B6 符号，不用上年滚存公式
        setBsRetainedEarningsUseStored(true);
        // B6.1：AL 上年已按 −GL!BU10−LFP 拆分；LFP 上年与拆分同源，避免本年 COS / 上年 PUR 时 Cap≠Net Assets
        setGlSuppressPlAfterTaxLastYear(suppressPlAfterTaxLastYear);
        setPLComputed({ afterTaxLastYear: lfpLastYear });
        setValuesInjected(true);
    }, [open, loading, byCodeReady, columnDefs, summaryCurrentPeriod, summaryPriorYear,
        byCode, sBsTree, sPlTree, setBsValues, setPlValues, setBsLastYearValues, setPlLastYearValues,
        setGlSuppressPlAfterTaxLastYear, setBsRetainedEarningsUseStored, setPlCotInputMode, setPLComputed]);

    useEffect(() => {
        if (!open && originalValuesRef.current) {
            const orig = originalValuesRef.current;
            setBsValues(orig.bsValues);
            setPlValues(orig.plValues);
            setBsLastYearValues(orig.bsLastYearValues);
            setPlLastYearValues(orig.plLastYearValues);
            setBsRetainedEarningsUseStored(orig.bsRetainedEarningsUseStored);
            setGlSuppressPlAfterTaxLastYear(false);
            originalValuesRef.current = null;
            setValuesInjected(false);
        }
    }, [open, setBsValues, setPlValues, setBsLastYearValues, setPlLastYearValues,
        setBsRetainedEarningsUseStored, setGlSuppressPlAfterTaxLastYear]);

    const [activeTab, setActiveTab] = React.useState<string>('BS');
    const handleTabChange = useCallback((key: string) => setActiveTab(key), []);

    const navigableFsCodes = useMemo(
        () => collectGlTinyNavigableFsCodes(columnDefs),
        [columnDefs],
    );

    const glExcelNameByFsCode = useMemo(
        () => buildGlExcelNameByFsCode(columnDefs, showClientAlias),
        [columnDefs, showClientAlias],
    );
    const glExcelNameZhByFsCode = useMemo(
        () => buildGlExcelNameZhByFsCode(columnDefs, showClientAlias),
        [columnDefs, showClientAlias],
    );

    const handleFsLineClick = useCallback((fsCode: string) => {
        onNavigateToTiny?.(fsCode);
    }, [onNavigateToTiny]);

    const bsContent = useMemo(() => (
        <BalanceSheet
            mode="fs"
            annualId={annualId}
            readOnly
            onFsLineClick={onNavigateToTiny ? handleFsLineClick : undefined}
            navigableFsCodes={navigableFsCodes}
            glExcelNameByFsCode={glExcelNameByFsCode}
            glExcelNameZhByFsCode={glExcelNameZhByFsCode}
            showClientAlias={showClientAlias}
        />
    ), [annualId, onNavigateToTiny, handleFsLineClick, navigableFsCodes, glExcelNameByFsCode, glExcelNameZhByFsCode, showClientAlias]);

    const plContent = useMemo(() => (
        <ProfitLoss
            mode="fs"
            annualId={annualId}
            readOnly
            onFsLineClick={onNavigateToTiny ? handleFsLineClick : undefined}
            navigableFsCodes={navigableFsCodes}
            glExcelNameByFsCode={glExcelNameByFsCode}
            glExcelNameZhByFsCode={glExcelNameZhByFsCode}
            showClientAlias={showClientAlias}
        />
    ), [annualId, onNavigateToTiny, handleFsLineClick, navigableFsCodes, glExcelNameByFsCode, glExcelNameZhByFsCode, showClientAlias]);

    const tabs = useMemo(() => ([
        {
            key: 'BS',
            label: 'Balance Sheet',
            icon: <BalanceSheetOutlinedIcon sx={{fontSize: 24}} />,
            content: bsContent,
        },
        {
            key: 'PL',
            label: 'Income Statement',
            icon: <ProfitLossOutlinedIcon sx={{fontSize: 24}} />,
            content: plContent,
        },
    ]), [bsContent, plContent]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '50vw' } }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                <Typography variant="h6" fontWeight={700}>FS Summary</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {onShowClientAliasChange && (
                        <Button
                            variant={showClientAlias ? 'contained' : 'outlined'}
                            size="small"
                            color={showClientAlias ? 'info' : 'inherit'}
                            startIcon={<BadgeIcon />}
                            onClick={() => onShowClientAliasChange(!showClientAlias)}
                            disabled={columnDefs.length === 0}
                            sx={{ textTransform: 'none' }}
                            title={showClientAlias ? 'Show standard account names' : 'Show client names'}
                        >
                            {showClientAlias ? 'Client names on' : 'Client names'}
                        </Button>
                    )}
                    <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
                </Stack>
            </Stack>
            <Divider />

            <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                {sError && (
                    <Alert severity="error" sx={{ mb: 1 }}>{sError}</Alert>
                )}

                {(loading || !valuesInjected) && (
                    <Box sx={{ p: 2 }}>
                        {[1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />
                        ))}
                    </Box>
                )}

                {!loading && valuesInjected && !sError && (
                    <SplitTabsPanel
                        tabs={tabs}
                        height={PANEL_HEIGHT}
                        selectedKey={activeTab}
                        onChange={handleTabChange}
                        renderAll
                        allowMaximize={false}
                    />
                )}
            </Box>
        </Drawer>
    );
}
